import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import {
    Button,
    Chip,
    Dropdown,
    Label,
    ListBox,
    Pagination,
} from '@heroui/react';
import { DataGrid, InlineSelect, Widget } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    ATTR_TYPE_COLUMN,
    ATTR_TYPE_ROW_ACTIONS,
    TYPES_PAGE_SIZE_OPTIONS,
    inputKindHasValues,
    type AttributeTypeRow,
    type AttributeTypeRowAction,
} from '../attributesMeta';
import type { useTypesTable } from './useTypesTable';

type TypesTableViewProps = {
    onDeleteType: (id: string) => void;
    onEditType: (id: string) => void;
    onManageValues: (id: string) => void;
    table: ReturnType<typeof useTypesTable>;
};

export const TypesTableView: React.FC<TypesTableViewProps> = ({
    onDeleteType,
    onEditType,
    onManageValues,
    table,
}) => {
    const { t } = useTranslation('pages');
    const {
        pageItems,
        page,
        totalPages,
        totalItems,
        pageWindow,
        rowsPerPage,
        sortDescriptor,
        onPageChange,
        onRowsPerPageChange,
        onSortChange,
    } = table;

    const copy = React.useMemo(
        () => ({
            tableAria: t('attributes.components.dictionary.typesTableAria'),
            actionsAria: t('attributes.components.dictionary.actionsAria'),
            previous: t('attributes.components.dictionary.pagination.previous'),
            next: t('attributes.components.dictionary.pagination.next'),
            perPage: t('attributes.components.dictionary.pagination.perPage'),
            columns: {
                name: t('attributes.components.dictionary.columns.name'),
                slug: t('attributes.components.dictionary.columns.slug'),
                inputKind: t(
                    'attributes.components.dictionary.columns.inputKind',
                ),
                isMultiSelect: t(
                    'attributes.components.dictionary.columns.isMultiSelect',
                ),
                valueCount: t(
                    'attributes.components.dictionary.columns.valueCount',
                ),
            },
            emptyTitle: t('attributes.components.dictionary.empty.types'),
            inputKinds: {
                Select: t('attributes.components.dictionary.inputKinds.Select'),
                Text: t('attributes.components.dictionary.inputKinds.Text'),
                Number: t('attributes.components.dictionary.inputKinds.Number'),
                Boolean: t(
                    'attributes.components.dictionary.inputKinds.Boolean',
                ),
            },
        }),
        [t],
    );

    const onRowAction = React.useCallback(
        (id: string, action: AttributeTypeRowAction) => {
            if (action === 'manageValues') {
                onManageValues(id);
                return;
            }
            if (action === 'edit') {
                onEditType(id);
                return;
            }
            onDeleteType(id);
        },
        [onDeleteType, onEditType, onManageValues],
    );

    const columns = React.useMemo<DataGridColumn<AttributeTypeRow>[]>(
        () => [
            {
                id: ATTR_TYPE_COLUMN.name,
                header: copy.columns.name,
                accessorKey: 'name',
                isRowHeader: true,
                allowsSorting: true,
                cell: (item) => (
                    <span
                        className="text-sm font-medium text-foreground"
                        {...getNodeTestIdProp(`attribute-type-name-${item.id}`)}
                    >
                        {item.name}
                    </span>
                ),
            },
            {
                id: ATTR_TYPE_COLUMN.slug,
                header: copy.columns.slug,
                accessorKey: 'slug',
                allowsSorting: true,
                cell: (item) => (
                    <span
                        className="font-mono text-xs text-muted"
                        {...getNodeTestIdProp(`attribute-type-slug-${item.id}`)}
                    >
                        {item.slug}
                    </span>
                ),
            },
            {
                id: ATTR_TYPE_COLUMN.inputKind,
                header: copy.columns.inputKind,
                accessorKey: 'inputKind',
                width: 120,
                allowsSorting: true,
                cell: (item) => (
                    <Chip
                        color="default"
                        size="sm"
                        variant="soft"
                        {...getNodeTestIdProp(`attribute-type-kind-${item.id}`)}
                    >
                        <Chip.Label>
                            {copy.inputKinds[item.inputKind]}
                        </Chip.Label>
                    </Chip>
                ),
            },
            {
                id: ATTR_TYPE_COLUMN.isMultiSelect,
                header: copy.columns.isMultiSelect,
                accessorKey: 'isMultiSelect',
                width: 110,
                align: 'center',
                cell: (item) => (
                    <span
                        className="flex w-full justify-center"
                        {...getNodeTestIdProp(
                            `attribute-type-multiselect-${item.id}`,
                        )}
                    >
                        {item.isMultiSelect ? (
                            <Icon
                                aria-label={copy.columns.isMultiSelect}
                                className="size-4 text-success-soft-foreground"
                                icon="gravity-ui:check"
                            />
                        ) : (
                            <span aria-hidden className="text-muted">
                                —
                            </span>
                        )}
                    </span>
                ),
            },
            {
                id: ATTR_TYPE_COLUMN.valueCount,
                header: copy.columns.valueCount,
                width: 90,
                align: 'end',
                cell: (item) => (
                    <span
                        className="text-sm text-foreground tabular-nums"
                        {...getNodeTestIdProp(
                            `attribute-type-values-${item.id}`,
                        )}
                    >
                        {inputKindHasValues(item.inputKind)
                            ? item.values.length
                            : '—'}
                    </span>
                ),
            },
            {
                id: ATTR_TYPE_COLUMN.actions,
                header: '',
                pinned: 'end',
                width: 40,
                align: 'center',
                cellClassName: 'bg-surface',
                headerClassName: 'bg-surface-secondary',
                cell: (item) => {
                    const actions = ATTR_TYPE_ROW_ACTIONS.filter(
                        (action) =>
                            action.id !== 'manageValues' ||
                            inputKindHasValues(item.inputKind),
                    );
                    return (
                        <div
                            className="flex justify-center"
                            {...getNodeTestIdProp(
                                `attribute-type-actions-${item.id}`,
                            )}
                        >
                            <Dropdown>
                                <Button
                                    aria-label={copy.actionsAria}
                                    isIconOnly
                                    size="sm"
                                    variant="tertiary"
                                    {...getNodeTestIdProp(
                                        `attribute-type-actions-trigger-${item.id}`,
                                    )}
                                >
                                    <Icon icon="gravity-ui:ellipsis-vertical" />
                                </Button>
                                <Dropdown.Popover className="min-w-[180px]">
                                    <Dropdown.Menu
                                        onAction={(key) =>
                                            onRowAction(
                                                item.id,
                                                key as AttributeTypeRowAction,
                                            )
                                        }
                                    >
                                        {actions.map((action) => (
                                            <Dropdown.Item
                                                id={action.id}
                                                key={action.id}
                                                textValue={t(action.labelKey)}
                                                variant={action.variant}
                                                {...getNodeTestIdProp(
                                                    `attribute-type-action-${action.id}-${item.id}`,
                                                )}
                                            >
                                                <Icon icon={action.icon} />
                                                <Label>
                                                    {t(action.labelKey)}
                                                </Label>
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown.Popover>
                            </Dropdown>
                        </div>
                    );
                },
            },
        ],
        [copy, onRowAction, t],
    );

    return (
        <Widget
            className="flex flex-col gap-0 overflow-hidden p-0"
            {...getNodeTestIdProp('attribute-types-widget')}
        >
            <DataGrid<AttributeTypeRow>
                aria-label={copy.tableAria}
                columns={columns}
                data={pageItems}
                getRowId={(item) => item.id}
                onSortChange={onSortChange}
                sortDescriptor={sortDescriptor}
                renderEmptyState={() => (
                    <div
                        className="flex flex-col items-center gap-3 py-12 text-muted"
                        {...getNodeTestIdProp('attribute-types-empty')}
                    >
                        <Icon
                            aria-hidden
                            className="size-8 text-default-500"
                            icon="gravity-ui:tag"
                        />
                        <span
                            className="text-sm"
                            {...getNodeTestIdProp(
                                'attribute-types-empty-title',
                            )}
                        >
                            {copy.emptyTitle}
                        </span>
                    </div>
                )}
                variant="primary"
                {...getNodeTestIdProp('attribute-types-grid')}
            />
            {totalItems > TYPES_PAGE_SIZE_OPTIONS[0]! ? (
                <div
                    className="flex flex-wrap items-center justify-between gap-3 border-t border-separator p-3"
                    {...getNodeTestIdProp('attribute-types-pagination')}
                >
                    <div className="flex items-center gap-2 text-xs text-muted">
                        <span>{copy.perPage}</span>
                        <InlineSelect
                            aria-label={copy.perPage}
                            onChange={(value) =>
                                value && onRowsPerPageChange(Number(value))
                            }
                            value={String(rowsPerPage)}
                            {...getNodeTestIdProp('attribute-types-per-page')}
                        >
                            <InlineSelect.Trigger>
                                <InlineSelect.Value />
                                <InlineSelect.Indicator />
                            </InlineSelect.Trigger>
                            <InlineSelect.Popover className="w-[96px]">
                                <ListBox>
                                    {TYPES_PAGE_SIZE_OPTIONS.map((size) => (
                                        <ListBox.Item
                                            id={String(size)}
                                            key={size}
                                            textValue={String(size)}
                                        >
                                            {size}
                                            <ListBox.ItemIndicator />
                                        </ListBox.Item>
                                    ))}
                                </ListBox>
                            </InlineSelect.Popover>
                        </InlineSelect>
                    </div>
                    {totalPages > 1 ? (
                        <Pagination size="sm">
                            <Pagination.Content>
                                <Pagination.Item>
                                    <Pagination.Previous
                                        aria-label={copy.previous}
                                        isDisabled={page === 1}
                                        onPress={() => onPageChange(page - 1)}
                                    >
                                        <Pagination.PreviousIcon />
                                    </Pagination.Previous>
                                </Pagination.Item>
                                {pageWindow.map((entry, index) =>
                                    entry === 'ellipsis' ? (
                                        <Pagination.Item
                                            key={`attribute-types-page-ellipsis-${index}`}
                                        >
                                            <Pagination.Ellipsis />
                                        </Pagination.Item>
                                    ) : (
                                        <Pagination.Item key={entry}>
                                            <Pagination.Link
                                                isActive={entry === page}
                                                onPress={() =>
                                                    onPageChange(entry)
                                                }
                                                {...getNodeTestIdProp(
                                                    `attribute-types-page-${entry}`,
                                                )}
                                            >
                                                {entry}
                                            </Pagination.Link>
                                        </Pagination.Item>
                                    ),
                                )}
                                <Pagination.Item>
                                    <Pagination.Next
                                        aria-label={copy.next}
                                        isDisabled={page === totalPages}
                                        onPress={() => onPageChange(page + 1)}
                                    >
                                        <Pagination.NextIcon />
                                    </Pagination.Next>
                                </Pagination.Item>
                            </Pagination.Content>
                        </Pagination>
                    ) : null}
                </div>
            ) : null}
        </Widget>
    );
};
